"use client"
import React, { useEffect } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Box } from "@mui/material";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

import Dashboard from "./pages/Dashboard";
import Instances from "./pages/Instances";
import Login from "./pages/Login";
import ApiDocs from "./pages/ApiDocs";

/* =========================
   Layout Base
========================= */
const Layout = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}
  >
    <Navbar />

    <Box
      component="main"
      sx={{
        flex: 1,
        overflow: "auto",
        pb: "300px", // espaço para o footer fixo
      }}
    >
      {children}
    </Box>

    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        boxShadow: "0px -4px 10px rgba(0,0,0,0.1)",
      }}
    >
      <Footer />
    </Box>
  </Box>
);

/* =========================
   Conteúdo com Auth
========================= */
const AppContent: React.FC = () => {
  const { validateToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const runAuthCheck = async () => {
      const token = localStorage.getItem("token");

      // Página pública
      if (location.pathname === "/login") {
        if (!token) return;

        const valid = await validateToken();
        if (valid) navigate("/", { replace: true });
        return;
      }

      // Rotas protegidas
      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      const valid = await validateToken();
      if (!valid) {
        navigate("/login", { replace: true });
      }
    };

    runAuthCheck();
  }, [validateToken, navigate, location.pathname]);

  return (
    <Routes>
      {/* Pública */}
      <Route path="/login" element={<Login />} />

      {/* Protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/instances"
        element={
          <ProtectedRoute>
            <Layout>
              <Instances />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/docs"
        element={
          <ProtectedRoute>
            <Layout>
              <ApiDocs />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/* =========================
   App Root
========================= */
const App: React.FC = () => (
  <AuthProvider>
    <AppContent />
  </AuthProvider>
);

export default App;